/**
 * Timezone helpers — the frontend always displays dates and filters
 * according to America/Sao_Paulo (BRT, UTC-3). The DB stores UTC.
 *
 * Do NOT use `new Date().toISOString()` for user-facing dates: it emits UTC
 * and will show "yesterday" for users in BRT between 21:00 and 24:00 local.
 * Use `todayIsoBrt()` / `formatIsoBrt(date)` instead.
 */

const BRT_TIME_ZONE = 'America/Sao_Paulo';

/**
 * Ensure an ISO string carries a timezone marker. Datetimes coming from
 * the backend that were saved before we enabled tz_aware=True in Motor
 * can arrive without any suffix; browsers then interpret them as LOCAL
 * time, breaking BRT display. Assume UTC when missing.
 */
export function withTz(iso) {
  if (!iso) return iso;
  if (typeof iso !== 'string') return iso;
  return /[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : `${iso}Z`;
}

/** Wrapper around `new Date(iso)` that first normalizes missing timezones. */
export function parseSafeDate(iso) {
  return iso ? new Date(withTz(iso)) : null;
}

// Static formatter (initialized once)
const brtDateFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: BRT_TIME_ZONE,
  year: 'numeric', month: '2-digit', day: '2-digit',
});

/** "YYYY-MM-DD" for the given Date (defaults to now) in BRT. */
export function formatIsoBrt(date = new Date()) {
  // en-CA emits YYYY-MM-DD natively
  return brtDateFormatter.format(date);
}

/** "YYYY-MM-DD" for today, in BRT. Preferred replacement for
 *  `new Date().toISOString().split('T')[0]`. */
export function todayIsoBrt() {
  return formatIsoBrt(new Date());
}

/** "YYYY-MM" for today's month in BRT. */
export function currentMonthBrt() {
  return todayIsoBrt().slice(0, 7);
}

/** Returns a JS Date object at 00:00 BRT for the given YYYY-MM-DD. */
export function brtDateStart(isoDate) {
  return new Date(`${isoDate}T00:00:00-03:00`);
}

const brtDateTimeFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: BRT_TIME_ZONE,
  year: 'numeric', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hour12: false,
});

/** Returns "YYYY-MM-DDTHH:MM" in BRT — the value expected by <input type="datetime-local">. */
export function toDatetimeLocalBrt(date) {
  const parts = brtDateTimeFormatter.formatToParts(date);
  const get = (t) => parts.find((p) => p.type === t)?.value || '';
  const hour = get('hour') === '24' ? '00' : get('hour');
  return `${get('year')}-${get('month')}-${get('day')}T${hour}:${get('minute')}`;
}
